import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReleaseTest } from '../data/release-tests-data';
import { type TestResultData, useTestResults } from '../hooks/useTestResults';
import { TestingDashboardHeader } from './TestingDashboardHeader';
import { VersionTestGroup } from './VersionTestGroup';

interface ReleaseTestingTabProps {
  filteredData: ReleaseTest[];
  onNavigateToChangelog: (version: string) => void;
  scrollToVersion?: string | null;
  noResults?: boolean;
  onClearFilters?: () => void;
}

export function ReleaseTestingTab({
  filteredData,
  onNavigateToChangelog,
  scrollToVersion,
  noResults,
  onClearFilters,
}: ReleaseTestingTabProps) {
  const { t } = useTranslation();
  const { data: allResults } = useTestResults();
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(filteredData[0] ? [filteredData[0].version] : [])
  );
  const versionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleVersion = useCallback((version: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(version)) next.delete(version);
      else next.add(version);
      return next;
    });
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (scrollToVersion) {
      setExpanded(prev => new Set([...prev, scrollToVersion]));
      setTimeout(() => {
        versionRefs.current[scrollToVersion]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
    }
  }, [scrollToVersion]);

  const totalFeatures = useMemo(
    () => filteredData.reduce((acc, rt) => acc + rt.features.length, 0),
    [filteredData]
  );

  // Bucket results by version once per `allResults` change, so the render
  // loop below is O(1) lookup per version instead of O(n) filter per version.
  const resultsByVersion = useMemo(() => {
    const map = new Map<string, TestResultData[]>();
    if (!allResults) return map;
    for (const r of allResults) {
      const bucket = map.get(r.version);
      if (bucket) bucket.push(r);
      else map.set(r.version, [r]);
    }
    return map;
  }, [allResults]);

  // Stable callback: prevents VersionTestGroup from re-rendering on every parent pass.
  const handleNavigate = useCallback(
    (version: string) => onNavigateToChangelog(version),
    [onNavigateToChangelog]
  );

  return (
    <div className="space-y-4">
      {/* Dashboard header — KPI stats, progress, team */}
      <TestingDashboardHeader totalFeatures={totalFeatures} allResults={allResults ?? []} />

      {noResults && (
        <div className="py-8 text-center">
          <p className="text-muted-foreground">{t('releaseHub.filters.noResults')}</p>
          {onClearFilters && (
            <button onClick={onClearFilters} className="mt-2 text-sm text-primary hover:underline">
              {t('releaseHub.filters.clearFilters')}
            </button>
          )}
        </div>
      )}

      {!noResults && filteredData.length === 0 && (
        <p className="py-8 text-center text-muted-foreground">{t('releaseHub.testing.noTests')}</p>
      )}

      {/* Version groups */}
      {filteredData.map(rt => (
        <VersionTestGroup
          key={rt.version}
          releaseTest={rt}
          results={resultsByVersion.get(rt.version) ?? []}
          isExpanded={expanded.has(rt.version)}
          onToggle={() => toggleVersion(rt.version)}
          onNavigateToChangelog={handleNavigate}
          ref={el => {
            versionRefs.current[rt.version] = el;
          }}
        />
      ))}
    </div>
  );
}
